import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { NativeQuote } from "../../dispersions/services/paymentsQuotes.service";
import type { RuntimeWallet } from "../../wallets/types/payments-wallets.types";
import type { SwapWallet } from "../services/swap.service";
import type { SwapFormData, SwapsState, SwapSuccessState } from "../types/swaps-api.types";

const initialState: SwapsState = {
  wallets: {
    byCurrency: {},
    availableCurrencies: [],
    isLoading: false,
    error: null,
    hasAccess: true,
    lastFetched: null,
  },
  quote: {
    data: null,
    isLoading: false,
    error: null,
    lastChangedAmountField: null,
    isExpired: false,
  },
  form: {
    formData: {
      origin_currency: "",
      destination_currency: "",
      amount_origin: "",
      amount_destination: "",
    },
    isSubmitting: false,
    submitError: null,
    idempotencyKey: null,
  },
  success: {
    isVisible: false,
    oldBalances: { origin: null, destination: null },
    newBalances: { origin: null, destination: null },
    summary: null,
  },
};

export const swapsSlice = createSlice({
  name: "swaps",
  initialState,
  reducers: {
    // Wallets (native runtime, keyed by currency)
    setWalletsLoading: (state, action: PayloadAction<boolean>) => {
      state.wallets.isLoading = action.payload;
    },
    setWallets: (
      state,
      action: PayloadAction<{ wallets: RuntimeWallet[]; availableCurrencies: string[] }>,
    ) => {
      state.wallets.byCurrency = Object.fromEntries(action.payload.wallets.map((w) => [w.currency, w]));
      state.wallets.availableCurrencies = action.payload.availableCurrencies;
      state.wallets.error = null;
      state.wallets.hasAccess = true;
      state.wallets.isLoading = false;
      state.wallets.lastFetched = Date.now();
    },
    setWalletsError: (state, action: PayloadAction<string>) => {
      state.wallets.error = action.payload;
      state.wallets.isLoading = false;
    },
    setWalletsAccessDenied: (state) => {
      state.wallets.hasAccess = false;
      state.wallets.isLoading = false;
      state.wallets.error = null;
    },

    // Quote (native, single-use)
    setQuoteLoading: (state, action: PayloadAction<boolean>) => {
      state.quote.isLoading = action.payload;
    },
    setQuoteData: (state, action: PayloadAction<NativeQuote>) => {
      state.quote.data = action.payload;
      state.quote.error = null;
      state.quote.isLoading = false;
      state.quote.isExpired = false;
    },
    setQuoteError: (state, action: PayloadAction<string | null>) => {
      state.quote.error = action.payload;
      state.quote.isLoading = false;
    },
    setQuoteExpired: (state, action: PayloadAction<boolean>) => {
      state.quote.isExpired = action.payload;
    },
    clearQuote: (state) => {
      state.quote = initialState.quote;
    },
    setLastChangedAmountField: (state, action: PayloadAction<"origin" | "destination" | null>) => {
      state.quote.lastChangedAmountField = action.payload;
    },

    // Form
    updateFormField: (state, action: PayloadAction<{ field: keyof SwapFormData; value: string }>) => {
      state.form.formData[action.payload.field] = action.payload.value;
    },
    setFormSubmitting: (state, action: PayloadAction<boolean>) => {
      state.form.isSubmitting = action.payload;
    },
    setFormSubmitError: (state, action: PayloadAction<string | null>) => {
      state.form.submitError = action.payload;
    },
    /**
     * Set once per attempt and kept across retries: reusing the key lets the backend replay the swap, while a
     * fresh key on the same quote would be rejected as already redeemed.
     */
    setIdempotencyKey: (state, action: PayloadAction<string | null>) => {
      state.form.idempotencyKey = action.payload;
    },
    resetForm: (state) => {
      state.form = initialState.form;
      state.quote = initialState.quote;
    },

    // Success screen
    setSwapSuccess: (
      state,
      action: PayloadAction<{
        oldBalances: SwapSuccessState["oldBalances"];
        newBalances: { origin: SwapWallet; destination: SwapWallet };
        summary: SwapSuccessState["summary"];
      }>,
    ) => {
      state.success.isVisible = true;
      state.success.oldBalances = action.payload.oldBalances;
      state.success.newBalances = action.payload.newBalances;
      state.success.summary = action.payload.summary;
    },
    clearSwapSuccess: (state) => {
      state.success = initialState.success;
      state.form = initialState.form;
      state.quote = initialState.quote;
    },

    resetSwapsState: () => initialState,
  },
});

export const {
  setWalletsLoading,
  setWallets,
  setWalletsError,
  setWalletsAccessDenied,
  setQuoteLoading,
  setQuoteData,
  setQuoteError,
  setQuoteExpired,
  clearQuote,
  setLastChangedAmountField,
  updateFormField,
  setFormSubmitting,
  setFormSubmitError,
  setIdempotencyKey,
  resetForm,
  setSwapSuccess,
  clearSwapSuccess,
  resetSwapsState,
} = swapsSlice.actions;
